/**
 * 購物車編輯時的「口味選取還原」純函數。
 *
 * 還原原則：**結構與名價一律以最新菜單為準，只把仍然合法的「選取狀態」併回去**。
 * 直接沿用購物車快照會讓畫面與送單資料停留在加入購物車當下的名稱、價格與售完狀態，
 * 使「重新選購」無法真正解除衝突（金額仍由 utils/order.js 以舊價計算）。
 *
 * 只依賴同層純函數（不 import Vuex / API / storeManager），對應 .claude/rules/testing.md 的純函數測試邊界。
 */

import { getQuantityRule } from './compareCart'

/**
 * 只保留「最新資料仍存在」的選取 id。
 *
 * 已被移除的細項若原樣還原，會在畫面上沒有對應可點的項目，
 * 卻仍佔用 mostQuantity 上限、並讓 leastQuantity 下限誤判為已滿足，
 * 而送單時又會被靜默略過（見 Detail.vue 組 optionSelect 的 `if (selected)`）。
 *
 * @param {object} latestOption 最新口味群組（需含 optionDetailList）
 * @param {string|string[]} cachedCount 快照選取值；單選為字串 id、複選為字串 id 陣列
 * @returns {string|string[]} 過濾後的選取值，型別與 latestOption.type 對應
 */
export function pickValidSelection(latestOption, cachedCount) {
	const validIds = new Set(
		(latestOption?.optionDetailList || []).map(detail => String(detail.id)),
	)

	// type 為真 = 複選
	if (latestOption?.type) {
		return (Array.isArray(cachedCount) ? cachedCount : [])
			.map(String)
			.filter(id => validIds.has(id))
	}

	const id =
		cachedCount == null || cachedCount === '' ? '' : String(cachedCount)
	return validIds.has(id) ? id : ''
}

/**
 * 以最新口味模板為底，套回快照中仍然存在且合法的選取狀態。
 *
 * 只還原 `count`（群組選取）與細項的 `count`（細項份數），
 * **不還原 name / price / availableForSale** —— 這些必須來自最新菜單。
 *
 * 會就地修改 latestOptionList，並回傳同一個參考以便串接。
 *
 * @param {object[]} latestOptionList 最新口味群組陣列
 * @param {object[]} cachedOptionList 快照口味群組陣列
 * @returns {object[]} latestOptionList
 */
export function applyCachedOptionSelection(latestOptionList, cachedOptionList) {
	;(latestOptionList || []).forEach(latestOption => {
		const cached = (cachedOptionList || []).find(
			cacheOption => String(cacheOption.id) === String(latestOption.id),
		)
		// 單選 / 複選互換代表題型已改，選取值無法沿用
		if (!cached || cached.type !== latestOption.type) {
			return
		}

		latestOption.count = pickValidSelection(latestOption, cached.count)

		latestOption.optionDetailList?.forEach(latestDetail => {
			const cachedDetail = cached.optionDetailList?.find(
				cacheDetail => String(cacheDetail.id) === String(latestDetail.id),
			)
			if (cachedDetail?.count != null) {
				latestDetail.count = cachedDetail.count
			}
		})
	})

	return latestOptionList
}

/** 深拷貝口味模板（資料皆為可序列化 JSON，與元件既有做法一致） */
function cloneOptionList(optionList) {
	return JSON.parse(JSON.stringify(optionList || []))
}

/**
 * 依份數重建「每份餐一組」的口味清單。
 *
 * 結構與名價一律取自最新模板，只把各份合法的選取併回 ——
 * 不可整份沿用快照的 optionSaveList，否則升級／套餐內層口味會停留在舊名稱與舊價格，
 * 送單時 utils/order.js 便以舊價計算（optionSelect[].optionDetailSelect.price）。
 *
 * @param {object[]} latestOptionList 最新口味模板（單份）
 * @param {object[][]} cachedOptionSaveList 快照的每份口味清單
 * @param {number} count 份數
 * @returns {object[][]} 長度為 count 的口味清單陣列
 */
export function buildOptionSaveListFromCache(
	latestOptionList,
	cachedOptionSaveList,
	count,
) {
	const size = Math.max(1, Number(count) || 1)
	const cached = Array.isArray(cachedOptionSaveList) ? cachedOptionSaveList : []

	return Array.from({ length: size }, (_, index) =>
		applyCachedOptionSelection(
			cloneOptionList(latestOptionList),
			cached[index],
		),
	)
}

/**
 * 複選口味的「已選數量」驗證（加入 / 更新購物車前）。
 *
 * 上限必須在此驗，不能只靠 Flavor.vue 把達上限的細項設為 disabled ——
 * 禁用只擋「新增勾選」，擋不住兩種既有的超量狀態：
 * 1. 從購物車「重新選購」還原選取後，店家已把上限調低（applyCachedOptionSelection
 *    只濾掉不存在的 id，不會替使用者決定砍哪一項）。
 * 2. 停留在詳情頁期間店家調低上限。
 * 放行超量會寫回購物車，下一步必被 compareCart 的 OPTION_QUANTITY_INVALID 標紅，
 * 形成「重新選購 → 更新成功 → 又被標紅」且使用者看不懂原因的迴圈。
 *
 * 規則來源與 compareCart 共用 getQuantityRule，確保「不限上限」同口徑。
 *
 * @param {object} option 最新口味群組（需含 type / leastQuantity / mostQuantity）
 * @param {number} checkedCount 有效已選數（須已排除售完與最新清單查無的細項）
 * @returns {{valid: boolean, errorMessage: string|null}}
 */
export function validateOptionQuantity(option, checkedCount) {
	// 單選由 required 分支覆蓋，不走數量規則
	if (!option?.type) {
		return { valid: true, errorMessage: null }
	}

	const { least, most } = getQuantityRule(option)

	if (checkedCount < least) {
		return {
			valid: false,
			errorMessage: `還需要選 ${least - checkedCount}項`,
		}
	}

	if (most !== null && checkedCount > most) {
		return {
			valid: false,
			errorMessage: `最多選 ${most}項，請取消 ${checkedCount - most}項`,
		}
	}

	return { valid: true, errorMessage: null }
}

/**
 * 套餐 / 升級群組的細項是否售完（fail-open：欄位未落地時視為可售）。
 *
 * 與 Flavor.vue 的 isSoldOut、compareCart 的 isSoldOut 同口徑 ——
 * 同一份 availableForSale 若在三處各自解讀，畫面、驗證與紅框就會互相打架。
 *
 * @param {object} detail 群組細項（combo 的 detailList / upgrade 的 upgradeContentDetailList 元素）
 * @returns {boolean}
 */
export function isContentDetailSoldOut(detail) {
	return detail?.availableForSale === false
}

/**
 * 套餐 / 升級群組的「選取還原」過濾（購物車重新選購時）。
 *
 * 兩件事都不能省：
 * 1. 濾掉最新清單查無的 id —— 畫面上沒有對應可點的項目，卻仍佔用份數上下限
 *    （與 pickValidSelection 對口味細項做的事相同）。
 * 2. 單選群組只留 1 筆 —— 畫面的 radio 綁在 select[0]，看不見也管不到 select[1] 之後。
 *    店家把上限從多份改回 1 份時，快照會帶著兩筆進來，radio 換選只改得動第一筆，
 *    殘留那筆會讓驗證永遠不過且使用者找不到它在哪，只能整筆刪掉重點。
 *
 * @param {object} content 最新群組（需含 mostQuantity 與細項清單）
 * @param {object[]} detailList 最新群組細項清單
 * @param {string[]} cachedSelect 快照選取 id 清單
 * @param {(detail: object) => string|number} getDetailId 取細項 id
 * @returns {string[]} 過濾後的選取 id 清單
 */
export function pickValidContentSelection(
	content,
	detailList,
	cachedSelect,
	getDetailId,
) {
	const validIds = new Set(
		(detailList || []).map(detail => String(getDetailId(detail))),
	)
	const picked = (Array.isArray(cachedSelect) ? cachedSelect : [])
		.map(String)
		.filter(id => validIds.has(id))

	return content?.mostQuantity === 1 ? picked.slice(0, 1) : picked
}

/**
 * 套餐 / 升級群組的「已選內容」驗證（加入 / 更新購物車前）。
 *
 * 售完必須在此驗，不能只靠 ComboCard / UpgradeCard 把售完細項設為 disabled ——
 * 禁用只擋「新增勾選」，擋不住從購物車「重新選購」還原進來、或停留在詳情頁期間
 * 才被店家設為售完的既有選取。放行會寫回購物車，下一步必被 compareCart 的
 * DETAIL_SOLD_OUT 標紅，形成「重新選購 → 更新成功 → 又被標紅」的迴圈
 * （與 validateOptionQuantity 擋既有超量是同一個理由，只是換成售完）。
 *
 * 售完的已選項同時不計入數量，否則它會充當必選份數，讓「必選 1 項」在
 * 唯一選取已售完時仍判定為已滿足。
 *
 * @param {object} params
 * @param {Array<{itemName?: string, count?: number, availableForSale?: boolean}>} params.detailList 最新群組細項清單
 * @param {string[]} params.select 已選 id 清單（畫面上的 select，字串 id）
 * @param {number} params.leastQuantity 下限
 * @param {number|null} params.mostQuantity 上限；null 為不限
 * @param {(detail: object) => string|number} params.getDetailId 取細項 id（combo 為 id、upgrade 為 upgradeContentDetailId）
 * @returns {{valid: boolean, errorMessage: string|null, checkedCount: number, soldOutSelectedNames: string[]}}
 */
export function validateContentSelection({
	detailList,
	select,
	leastQuantity,
	mostQuantity,
	getDetailId,
}) {
	const selectedIds = new Set((Array.isArray(select) ? select : []).map(String))
	const selectedDetails = (detailList || []).filter(detail =>
		selectedIds.has(String(getDetailId(detail))),
	)

	/** @type {string[]} */
	const soldOutSelectedNames = []
	let checkedCount = 0

	selectedDetails.forEach(detail => {
		if (isContentDetailSoldOut(detail)) {
			soldOutSelectedNames.push(detail.itemName)
			return
		}
		checkedCount += Number(detail.count) || 0
	})

	if (soldOutSelectedNames.length > 0) {
		return {
			valid: false,
			errorMessage: `${soldOutSelectedNames.join('、')} 已售完，請重新選擇`,
			checkedCount,
			soldOutSelectedNames,
		}
	}

	if (checkedCount < (Number(leastQuantity) || 0)) {
		return {
			valid: false,
			errorMessage: '請選擇餐點',
			checkedCount,
			soldOutSelectedNames,
		}
	}

	if (mostQuantity !== null && mostQuantity !== undefined) {
		if (checkedCount > mostQuantity) {
			return {
				valid: false,
				errorMessage: `最多選 ${mostQuantity}項，請取消 ${
					checkedCount - mostQuantity
				}項`,
				checkedCount,
				soldOutSelectedNames,
			}
		}
	}

	return {
		valid: true,
		errorMessage: null,
		checkedCount,
		soldOutSelectedNames,
	}
}

/**
 * 群組還可以再選幾份（扣掉「其他已選項」已佔用的份數）。
 *
 * 售完的已選項不佔用上限 —— 它已被 validateContentSelection 擋下，本來就得換掉；
 * 若讓它佔額度，使用者連「改選其他可售項」都會被禁用，複選群組就完全無路可走。
 * （與 Flavor.vue disabledOption 排除售完已選項是同一個道理）
 *
 * @param {object} params
 * @param {object[]} params.detailList 最新群組細項清單
 * @param {string[]} params.select 已選 id 清單
 * @param {number|null} params.mostQuantity 上限；falsy 為不限
 * @param {(detail: object) => string|number} params.getDetailId 取細項 id
 * @param {string|number} params.excludeDetailId 計算對象自身的 id（不計入已佔用）
 * @returns {number|undefined} 剩餘可選份數；不限上限時為 undefined（呼叫端據此不禁用）
 */
export function getContentRemainingCount({
	detailList,
	select,
	mostQuantity,
	getDetailId,
	excludeDetailId,
}) {
	if (!mostQuantity) {
		return undefined
	}

	const selectedIds = new Set((Array.isArray(select) ? select : []).map(String))
	const used = (detailList || [])
		.filter(detail => {
			const id = getDetailId(detail)
			if (String(id) === String(excludeDetailId)) {
				return false
			}
			return selectedIds.has(String(id)) && !isContentDetailSoldOut(detail)
		})
		.reduce((total, detail) => total + (Number(detail.count) || 0), 0)

	return mostQuantity - used
}

/**
 * 讓「各份是否展開口味」的長度與份數一致，缺值預設展開。
 *
 * @param {boolean[]} cachedIsShowFlavor 快照值
 * @param {number} count 份數
 * @returns {boolean[]}
 */
export function normalizeIsShowFlavor(cachedIsShowFlavor, count) {
	const size = Math.max(1, Number(count) || 1)

	return Array.from({ length: size }, (_, index) =>
		Array.isArray(cachedIsShowFlavor) && cachedIsShowFlavor[index] != null
			? cachedIsShowFlavor[index]
			: true,
	)
}
